import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Paper,
    Avatar,
    Button,
    Divider,
    LinearProgress,
    TextField,
    Alert,
    IconButton,
    Tooltip,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Delete,
    CheckCircle,
    Cancel,
    Home,
    ArrowBack,
    DeleteOutline,
    Fastfood,
    Checkroom,
    Calculate,
    LocationOn,
    Phone,
    Schedule,
    TrendingUp,
    Info,
    PlayArrow,
    MonetizationOn,
    Group,
    Public
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const GeneralWastePage = () => {
    const [monthlyWaste, setMonthlyWaste] = useState(50);
    const [activeStep, setActiveStep] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const generalItems = [
        {
            category: "Household Items",
            icon: <Home sx={{ fontSize: 40, color: '#757575' }} />,
            items: ["Broken ceramics", "Vacuum cleaner bags", "Cat litter", "Diapers", "Cigarette butts", "Broken glass", "Worn textiles", "Rubber items"],
            color: "#757575",
            decompositionTime: "10-1000+ years",
            percentage: 35
        },
        {
            category: "Food Packaging",
            icon: <Fastfood sx={{ fontSize: 40, color: '#8d6e63' }} />,
            items: ["Chip bags", "Candy wrappers", "Takeout containers", "Disposable cups", "Pizza boxes (greasy)", "Aluminum foil (dirty)", "Plastic utensils"],
            color: "#8d6e63",
            decompositionTime: "20-450 years",
            percentage: 25
        },
        {
            category: "Personal Items",
            icon: <Checkroom sx={{ fontSize: 40, color: '#607d8b' }} />,
            items: ["Worn-out shoes", "Old toothbrushes", "Hair brushes", "Makeup containers", "Used tissues", "Sanitary products", "Contact lenses"],
            color: "#607d8b",
            decompositionTime: "5-40 years",
            percentage: 20
        },
        {
            category: "Mixed Materials",
            icon: <DeleteOutline sx={{ fontSize: 40, color: '#795548' }} />,
            items: ["Laminated paper", "Carbon paper", "Thermal paper", "Stickers", "Tape", "Bubble wrap", "Wax paper"],
            color: "#795548",
            decompositionTime: "2-20 years",
            percentage: 20
        }
    ];

    const reductionTips = [
        {
            title: "Buy in Bulk",
            description: "Reduce packaging waste by purchasing larger quantities",
            icon: "📦",
            impact: "30% less packaging",
            difficulty: "Easy"
        },
        {
            title: "Choose Reusable",
            description: "Opt for reusable items instead of disposable ones",
            icon: "♻️",
            impact: "50% waste reduction",
            difficulty: "Easy"
        },
        {
            title: "Repair Before Replace",
            description: "Fix items when possible instead of throwing them away",
            icon: "🔧",
            impact: "40% less waste",
            difficulty: "Medium"
        },
        {
            title: "Digital Receipts",
            description: "Choose electronic receipts to reduce paper waste",
            icon: "📱",
            impact: "100% paper saved",
            difficulty: "Easy"
        },
        {
            title: "Minimize Packaging",
            description: "Choose products with minimal or recyclable packaging",
            icon: "🌱",
            impact: "25% less waste",
            difficulty: "Easy"
        },
        {
            title: "Quality over Quantity",
            description: "Buy durable items that last longer",
            icon: "⭐",
            impact: "60% longer lifespan",
            difficulty: "Medium"
        }
    ];

    const bangladeshStats = {
        dailyWaste: "47,000 tons",
        recyclingRate: "15%",
        landfillLife: "20 years",
        wasteGrowth: "4.5% annually",
        populationServed: "60%",
        jobs: "150,000+"
    };

    const disposalLocations = [
        {
            city: "Dhaka",
            locations: ["Matuail Landfill", "Aminbazar Landfill", "DNCC Collection Points"],
            contact: "+880-2-8391215",
            schedule: "Daily 6 AM - 8 PM"
        },
        {
            city: "Chittagong",
            locations: ["Halishahar Dumping Ground", "CCC Collection Centers"],
            contact: "+880-31-2853333",
            schedule: "Daily 7 AM - 7 PM"
        },
        {
            city: "Sylhet",
            locations: ["Mogla Bazar Dumping Site", "SCC Waste Centers"],
            contact: "+880-821-714491",
            schedule: "Daily 6 AM - 6 PM"
        }
    ];

    const wasteSteps = [
        {
            label: "Reduce First",
            description: "Before disposal, consider if you can reduce consumption or find alternatives"
        },
        {
            label: "Check for Recyclables",
            description: "Separate any recyclable materials from general waste"
        },
        {
            label: "Proper Bagging",
            description: "Use appropriate bags and don't overfill containers"
        },
        {
            label: "Schedule Pickup",
            description: "Follow local collection schedules and guidelines"
        }
    ];

    const calculateCost = () => {
        const costPerKg = 5; // BDT per kg
        const annualCost = monthlyWaste * 12 * costPerKg;
        const reductionSavings = annualCost * 0.3; // 30% potential reduction
        return { annualCost, reductionSavings };
    };

    const { annualCost, reductionSavings } = calculateCost();

    const dosDonts = {
        dos: [
            "Use designated general waste bins",
            "Bag waste properly to prevent spillage",
            "Compress bulky items when possible",
            "Follow local collection schedules",
            "Consider waste reduction alternatives first",
            "Keep waste covered until collection",
            "Separate hazardous materials"
        ],
        donts: [
            "Mix general waste with recyclables",
            "Put hazardous materials in general waste",
            "Overfill waste containers",
            "Leave waste bags outside collection areas",
            "Ignore weight limits for waste bags",
            "Burn or bury waste illegally",
            "Dump waste in water bodies"
        ]
    };

    const landfillImpacts = [
        { impact: "Takes up valuable land space", severity: "high", details: "1 ton = 1.5 cubic meters" },
        { impact: "Produces methane gas emissions", severity: "high", details: "25x more potent than CO2" },
        { impact: "Potential groundwater contamination", severity: "medium", details: "Leachate can travel 100m+" },
        { impact: "Long decomposition times", severity: "high", details: "Some items take 1000+ years" },
        { impact: "Attracts pests and creates odors", severity: "low", details: "Health and aesthetic concerns" },
        { impact: "Soil contamination", severity: "medium", details: "Heavy metals and chemicals" }
    ];

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #424242 0%, #616161 100%)',
                    color: 'white',
                    py: { xs: 4, sm: 6, md: 8 },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'radial-gradient(circle at 40% 60%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                        zIndex: 1
                    }
                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                    <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Button
                                component={Link}
                                to="/"
                                startIcon={<ArrowBack />}
                                sx={{
                                    color: 'white',
                                    mb: { xs: 2, sm: 3 },
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                }}
                            >
                                Back to Home
                            </Button>

                            <Alert
                                severity="info"
                                sx={{
                                    mb: { xs: 2, sm: 3 },
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                    '& .MuiAlert-icon': { color: '#757575' },
                                    '& .MuiAlert-message': { fontSize: { xs: '0.875rem', sm: '1rem' } }
                                }}
                            >
                                <Typography fontWeight={600} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    In Bangladesh, each person generates approximately 0.5 kg of waste daily!
                                </Typography>
                            </Alert>

                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: { xs: 2, sm: 3 },
                                flexDirection: { xs: 'column', sm: 'row' },
                                textAlign: { xs: 'center', sm: 'left' }
                            }}>
                                <Avatar
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        width: { xs: 60, sm: 70, md: 80 },
                                        height: { xs: 60, sm: 70, md: 80 },
                                        mr: { xs: 0, sm: 3 },
                                        mb: { xs: 2, sm: 0 }
                                    }}
                                >
                                    <Delete sx={{ fontSize: { xs: 35, sm: 45, md: 50 }, color: 'white' }} />
                                </Avatar>
                                <Box>
                                    <Typography
                                        variant="h2"
                                        fontWeight={800}
                                        gutterBottom
                                        sx={{
                                            fontSize: { xs: '2rem', sm: '2.5rem', md: '3.75rem' },
                                            lineHeight: { xs: 1.2, sm: 1.167 }
                                        }}
                                    >
                                        General Waste
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            opacity: 0.9,
                                            fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
                                        }}
                                    >
                                        Last resort disposal for non-recyclable materials
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                                    lineHeight: 1.6,
                                    opacity: 0.95
                                }}
                            >
                                General waste includes items that cannot be recycled, composted, or classified as hazardous.
                                While this waste typically goes to landfills, we should always try to reduce, reuse, and recycle first.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: { xs: 200, sm: 250, md: 300 },
                                    position: 'relative'
                                }}
                            >
                                <DeleteOutline sx={{
                                    fontSize: { xs: 120, sm: 160, md: 200 },
                                    color: 'rgba(255,255,255,0.3)'
                                }} />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
                {/* Bangladesh Waste Statistics */}
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        mb: { xs: 4, sm: 6, md: 8 },
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                        border: '2px solid #2196f3'
                    }}
                >
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        textAlign="center"
                        gutterBottom
                        sx={{
                            mb: { xs: 2, sm: 3, md: 4 },
                            color: '#1565c0',
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                        }}
                    >
                        🇧🇩 Bangladesh Waste Statistics
                    </Typography>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {Object.entries(bangladeshStats).map(([key, value], index) => (
                            <Grid item xs={6} sm={4} md={2} key={index}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#1976d2"
                                        gutterBottom
                                        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
                                    >
                                        {value}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            textTransform: 'capitalize',
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                        }}
                                    >
                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                {/* Cost Calculator */}
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        mb: { xs: 4, sm: 6, md: 8 },
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
                        border: '2px solid #9c27b0'
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: { xs: 2, sm: 3 },
                        flexDirection: { xs: 'column', sm: 'row' },
                        textAlign: { xs: 'center', sm: 'left' }
                    }}>
                        <Calculate sx={{
                            color: '#9c27b0',
                            fontSize: { xs: 30, sm: 35, md: 40 },
                            mr: { xs: 0, sm: 2 },
                            mb: { xs: 1, sm: 0 }
                        }} />
                        <Typography
                            variant="h4"
                            fontWeight={700}
                            color="#7b1fa2"
                            sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
                        >
                            💰 Personal Waste Cost Calculator
                        </Typography>
                    </Box>
                    <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Monthly Waste (kg)"
                                type="number"
                                value={monthlyWaste}
                                onChange={(e) => setMonthlyWaste(Number(e.target.value))}
                                fullWidth
                                sx={{ mb: { xs: 2, md: 0 } }}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-around',
                                textAlign: 'center',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: { xs: 2, sm: 0 }
                            }}>
                                <Box>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#9c27b0"
                                        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
                                    >
                                        ৳{annualCost}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                    >
                                        Annual Cost
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#4caf50"
                                        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
                                    >
                                        ৳{reductionSavings}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                    >
                                        Potential Savings
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {/* General Waste Categories with Progress */}
                <Typography
                    variant="h3"
                    fontWeight={700}
                    textAlign="center"
                    gutterBottom
                    sx={{
                        mb: { xs: 3, sm: 4, md: 6 },
                        color: '#424242',
                        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
                    }}
                >
                    Types of General Waste
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
                    {generalItems.map((category, index) => (
                        <Grid item xs={12} lg={6} key={index}>
                            <Card
                                elevation={4}
                                sx={{
                                    height: '100%',
                                    borderRadius: 4,
                                    transition: 'all 0.3s ease',
                                    border: '2px solid transparent',
                                    '&:hover': {
                                        transform: { xs: 'none', sm: 'translateY(-8px)' },
                                        boxShadow: '0 12px 40px rgba(117, 117, 117, 0.2)',
                                        borderColor: category.color
                                    }
                                }}
                            >
                                <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: { xs: 2, sm: 3 },
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        textAlign: { xs: 'center', sm: 'left' }
                                    }}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            mb: { xs: 2, sm: 0 }
                                        }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: `${category.color}20`,
                                                    width: { xs: 50, sm: 55, md: 60 },
                                                    height: { xs: 50, sm: 55, md: 60 },
                                                    mr: { xs: 0, sm: 2 },
                                                    mb: { xs: 1, sm: 0 }
                                                }}
                                            >
                                                {React.cloneElement(category.icon, {
                                                    sx: { fontSize: { xs: 30, sm: 35, md: 40 }, color: category.color }
                                                })}
                                            </Avatar>
                                            <Box>
                                                <Typography
                                                    variant="h5"
                                                    fontWeight={600}
                                                    color={category.color}
                                                    sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}
                                                >
                                                    {category.category}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                                >
                                                    {category.decompositionTime}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Chip
                                            label={`${category.percentage}%`}
                                            color="primary"
                                            variant="outlined"
                                            size={isMobile ? "small" : "medium"}
                                        />
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                mb: 1,
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                            }}
                                        >
                                            Composition in household waste:
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={category.percentage}
                                            sx={{
                                                height: { xs: 6, sm: 8 },
                                                borderRadius: 4,
                                                backgroundColor: `${category.color}20`,
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor: category.color
                                                }
                                            }}
                                        />
                                    </Box>

                                    <Grid container spacing={1}>
                                        {category.items.map((item, itemIndex) => (
                                            <Grid item xs={12} sm={6} key={itemIndex}>
                                                <Chip
                                                    label={item}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        borderColor: category.color,
                                                        color: category.color,
                                                        width: '100%',
                                                        justifyContent: 'flex-start',
                                                        mb: 1,
                                                        fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                                        height: { xs: 28, sm: 32 },
                                                        '&:hover': {
                                                            backgroundColor: `${category.color}10`
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Enhanced Waste Reduction Tips */}
                <Typography
                    variant="h3"
                    fontWeight={700}
                    textAlign="center"
                    gutterBottom
                    sx={{
                        mb: { xs: 3, sm: 4, md: 6 },
                        color: '#424242',
                        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
                    }}
                >
                    Smart Waste Reduction Strategies
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
                    {reductionTips.map((tip, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper
                                elevation={3}
                                sx={{
                                    p: { xs: 2, sm: 3 },
                                    borderRadius: 4,
                                    height: '100%',
                                    background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                                    transition: 'all 0.3s ease',
                                    border: '2px solid transparent',
                                    '&:hover': {
                                        transform: { xs: 'none', sm: 'translateY(-4px)' },
                                        boxShadow: '0 8px 25px rgba(117, 117, 117, 0.15)',
                                        borderColor: '#4caf50'
                                    }
                                }}
                            >
                                <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            mb: 2,
                                            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                                        }}
                                    >
                                        {tip.icon}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        fontWeight={600}
                                        gutterBottom
                                        color="#424242"
                                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
                                    >
                                        {tip.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            mb: 2,
                                            fontSize: { xs: '0.875rem', sm: '1rem' }
                                        }}
                                    >
                                        {tip.description}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 2,
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: { xs: 1, sm: 0 }
                                }}>
                                    <Chip
                                        label={tip.impact}
                                        color="success"
                                        size="small"
                                        icon={<TrendingUp />}
                                        sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                                    />
                                    <Chip
                                        label={tip.difficulty}
                                        color={tip.difficulty === 'Easy' ? 'success' : 'warning'}
                                        size="small"
                                        sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                                    />
                                </Box>

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<PlayArrow />}
                                    sx={{
                                        mt: 2,
                                        fontSize: { xs: '0.875rem', sm: '1rem' }
                                    }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Start Today
                                </Button>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* Disposal Process Guide */}
                <Typography
                    variant="h3"
                    fontWeight={700}
                    textAlign="center"
                    gutterBottom
                    sx={{
                        mb: { xs: 3, sm: 4, md: 6 },
                        color: '#424242',
                        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
                    }}
                >
                    Step-by-Step Disposal Guide
                </Typography>

                <Paper sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    mb: { xs: 4, sm: 6, md: 8 },
                    borderRadius: 4
                }}>
                    <Stepper activeStep={activeStep} orientation="vertical">
                        {wasteSteps.map((step, index) => (
                            <Step key={step.label}>
                                <StepLabel
                                    optional={
                                        index === wasteSteps.length - 1 ? (
                                            <Typography
                                                variant="caption"
                                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                            >
                                                Last step
                                            </Typography>
                                        ) : null
                                    }
                                >
                                    <Typography
                                        variant="h6"
                                        fontWeight={600}
                                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
                                    >
                                        {step.label}
                                    </Typography>
                                </StepLabel>
                                <StepContent>
                                    <Typography
                                        sx={{
                                            mb: 2,
                                            fontSize: { xs: '0.875rem', sm: '1rem' }
                                        }}
                                    >
                                        {step.description}
                                    </Typography>
                                    <Box sx={{ mb: 2 }}>
                                        <Button
                                            variant="contained"
                                            onClick={() => setActiveStep(index + 1)}
                                            sx={{ mt: 1, mr: 1 }}
                                            disabled={index === wasteSteps.length - 1}
                                            size={isMobile ? "small" : "medium"}
                                        >
                                            {index === wasteSteps.length - 1 ? 'Finish' : 'Continue'}
                                        </Button>
                                        <Button
                                            disabled={index === 0}
                                            onClick={() => setActiveStep(index - 1)}
                                            sx={{ mt: 1, mr: 1 }}
                                            size={isMobile ? "small" : "medium"}
                                        >
                                            Back
                                        </Button>
                                    </Box>
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>
                    {activeStep === wasteSteps.length && (
                        <Paper square elevation={0} sx={{ p: { xs: 2, sm: 3 } }}>
                            <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                All steps completed - you're ready for proper waste disposal!
                            </Typography>
                            <Button
                                onClick={() => setActiveStep(0)}
                                sx={{ mt: 1, mr: 1 }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Reset Guide
                            </Button>
                        </Paper>
                    )}
                </Paper>

                {/* Local Disposal Information */}
                <Typography
                    variant="h3"
                    fontWeight={700}
                    textAlign="center"
                    gutterBottom
                    sx={{
                        mb: { xs: 3, sm: 4, md: 6 },
                        color: '#424242',
                        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
                    }}
                >
                    🗺️ Local Disposal Centers
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
                    {disposalLocations.map((location, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Card
                                elevation={3}
                                sx={{
                                    p: { xs: 2, sm: 3 },
                                    borderRadius: 4,
                                    height: '100%',
                                    border: '2px solid #2196f3'
                                }}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    textAlign: { xs: 'center', sm: 'left' }
                                }}>
                                    <LocationOn sx={{
                                        color: '#2196f3',
                                        mr: { xs: 0, sm: 1 },
                                        mb: { xs: 1, sm: 0 }
                                    }} />
                                    <Typography
                                        variant="h5"
                                        fontWeight={600}
                                        color="#1976d2"
                                        sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}
                                    >
                                        {location.city}
                                    </Typography>
                                </Box>

                                <List dense>
                                    {location.locations.map((loc, idx) => (
                                        <ListItem key={idx} sx={{ px: 0 }}>
                                            <ListItemText
                                                primary={loc}
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontSize: { xs: '0.875rem', sm: '1rem' }
                                                    }
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 1,
                                    flexWrap: 'wrap'
                                }}>
                                    <Phone sx={{ color: '#757575', mr: 1, fontSize: 20 }} />
                                    <Typography
                                        variant="body2"
                                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                    >
                                        {location.contact}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap'
                                }}>
                                    <Schedule sx={{ color: '#757575', mr: 1, fontSize: 20 }} />
                                    <Typography
                                        variant="body2"
                                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                    >
                                        {location.schedule}
                                    </Typography>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Enhanced Environmental Impact */}
                <Typography
                    variant="h3"
                    fontWeight={700}
                    textAlign="center"
                    gutterBottom
                    sx={{
                        mb: { xs: 3, sm: 4, md: 6 },
                        color: '#424242',
                        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
                    }}
                >
                    Environmental Impact Details
                </Typography>

                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                        border: '2px solid #ff9800',
                        mb: { xs: 4, sm: 6, md: 8 }
                    }}
                >
                    <Typography
                        variant="h5"
                        fontWeight={600}
                        gutterBottom
                        color="#e65100"
                        textAlign="center"
                        sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}
                    >
                        ⚠️ Landfill Environmental Concerns
                    </Typography>
                    <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 2 }}>
                        {landfillImpacts.map((item, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    textAlign: { xs: 'center', sm: 'left' }
                                }}>
                                    <Chip
                                        label={item.severity.toUpperCase()}
                                        color={item.severity === 'high' ? 'error' : item.severity === 'medium' ? 'warning' : 'default'}
                                        size="small"
                                        sx={{
                                            mr: { xs: 0, sm: 2 },
                                            mb: { xs: 1, sm: 0 },
                                            minWidth: 80,
                                            fontSize: { xs: '0.75rem', sm: '0.8125rem' }
                                        }}
                                    />
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography
                                            variant="body1"
                                            fontWeight={500}
                                            sx={{
                                                mb: 0.5,
                                                fontSize: { xs: '0.875rem', sm: '1rem' }
                                            }}
                                        >
                                            {item.impact}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        >
                                            {item.details}
                                        </Typography>
                                    </Box>
                                    <Tooltip title="Learn more">
                                        <IconButton size="small">
                                            <Info />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                {/* Disposal Guidelines */}
                <Typography
                    variant="h3"
                    fontWeight={700}
                    textAlign="center"
                    gutterBottom
                    sx={{
                        mb: { xs: 3, sm: 4, md: 6 },
                        color: '#424242',
                        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
                    }}
                >
                    Disposal Guidelines
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: { xs: 2, sm: 3, md: 4 },
                                borderRadius: 4,
                                background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c8 100%)',
                                border: '2px solid #4caf50'
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: { xs: 2, sm: 3 },
                                flexDirection: { xs: 'column', sm: 'row' },
                                textAlign: { xs: 'center', sm: 'left' }
                            }}>
                                <CheckCircle sx={{
                                    color: '#4caf50',
                                    fontSize: { xs: 30, sm: 35, md: 40 },
                                    mr: { xs: 0, sm: 2 },
                                    mb: { xs: 1, sm: 0 }
                                }} />
                                <Typography
                                    variant="h4"
                                    fontWeight={700}
                                    color="#2e7d32"
                                    sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
                                >
                                    DO's
                                </Typography>
                            </Box>
                            <List>
                                {dosDonts.dos.map((item, index) => (
                                    <ListItem key={index} sx={{ px: 0 }}>
                                        <ListItemIcon>
                                            <CheckCircle sx={{ color: '#4caf50' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item}
                                            sx={{
                                                '& .MuiListItemText-primary': {
                                                    fontWeight: 500,
                                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                                }
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: { xs: 2, sm: 3, md: 4 },
                                borderRadius: 4,
                                background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                                border: '2px solid #f44336'
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: { xs: 2, sm: 3 },
                                flexDirection: { xs: 'column', sm: 'row' },
                                textAlign: { xs: 'center', sm: 'left' }
                            }}>
                                <Cancel sx={{
                                    color: '#f44336',
                                    fontSize: { xs: 30, sm: 35, md: 40 },
                                    mr: { xs: 0, sm: 2 },
                                    mb: { xs: 1, sm: 0 }
                                }} />
                                <Typography
                                    variant="h4"
                                    fontWeight={700}
                                    color="#c62828"
                                    sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
                                >
                                    DON'Ts
                                </Typography>
                            </Box>
                            <List>
                                {dosDonts.donts.map((item, index) => (
                                    <ListItem key={index} sx={{ px: 0 }}>
                                        <ListItemIcon>
                                            <Cancel sx={{ color: '#f44336' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item}
                                            sx={{
                                                '& .MuiListItemText-primary': {
                                                    fontWeight: 500,
                                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                                }
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Enhanced Call to Action with Multiple Options */}
                <Paper
                    elevation={6}
                    sx={{
                        p: { xs: 3, sm: 4, md: 6 },
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        variant="h3"
                        fontWeight={700}
                        gutterBottom
                        color="#424242"
                        sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}
                    >
                        Take Action Today
                    </Typography>
                    <Divider sx={{
                        my: 3,
                        maxWidth: 200,
                        mx: 'auto',
                        backgroundColor: '#757575'
                    }} />

                    <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mb: { xs: 3, sm: 4 } }}>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                                <MonetizationOn sx={{
                                    fontSize: { xs: 40, sm: 44, md: 48 },
                                    color: '#4caf50',
                                    mb: 2
                                }} />
                                <Typography
                                    variant="h5"
                                    fontWeight={600}
                                    gutterBottom
                                    sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}
                                >
                                    Save Money
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                >
                                    Reduce waste to lower disposal costs and save up to ৳{reductionSavings} annually
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                                <Public sx={{
                                    fontSize: { xs: 40, sm: 44, md: 48 },
                                    color: '#2196f3',
                                    mb: 2
                                }} />
                                <Typography
                                    variant="h5"
                                    fontWeight={600}
                                    gutterBottom
                                    sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}
                                >
                                    Help Environment
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                >
                                    Reduce landfill burden and protect Bangladesh's limited land resources
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                                <Group sx={{
                                    fontSize: { xs: 40, sm: 44, md: 48 },
                                    color: '#ff9800',
                                    mb: 2
                                }} />
                                <Typography
                                    variant="h5"
                                    fontWeight={600}
                                    gutterBottom
                                    sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}
                                >
                                    Build Community
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                >
                                    Inspire others and create a cleaner, healthier community for everyone
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Waste Hierarchy - Enhanced */}
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        gutterBottom
                        color="#424242"
                        sx={{
                            mb: { xs: 3, sm: 4 },
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                        }}
                    >
                        Remember the Waste Hierarchy
                    </Typography>
                    <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ position: 'relative' }}>
                                <Typography
                                    variant="h2"
                                    fontWeight={800}
                                    color="#4caf50"
                                    gutterBottom
                                    sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' } }}
                                >
                                    1st
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color="text.secondary"
                                    sx={{
                                        mb: 2,
                                        fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' }
                                    }}
                                >
                                    REDUCE consumption and waste generation
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={100}
                                    sx={{
                                        height: { xs: 6, sm: 8 },
                                        borderRadius: 4,
                                        backgroundColor: '#e8f5e8',
                                        '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' }
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ position: 'relative' }}>
                                <Typography
                                    variant="h2"
                                    fontWeight={800}
                                    color="#2196f3"
                                    gutterBottom
                                    sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' } }}
                                >
                                    2nd
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color="text.secondary"
                                    sx={{
                                        mb: 2,
                                        fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' }
                                    }}
                                >
                                    REUSE items whenever possible
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={75}
                                    sx={{
                                        height: { xs: 6, sm: 8 },
                                        borderRadius: 4,
                                        backgroundColor: '#e3f2fd',
                                        '& .MuiLinearProgress-bar': { backgroundColor: '#2196f3' }
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ position: 'relative' }}>
                                <Typography
                                    variant="h2"
                                    fontWeight={800}
                                    color="#ff9800"
                                    gutterBottom
                                    sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' } }}
                                >
                                    3rd
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color="text.secondary"
                                    sx={{
                                        mb: 2,
                                        fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' }
                                    }}
                                >
                                    RECYCLE materials that can be processed
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={50}
                                    sx={{
                                        height: { xs: 6, sm: 8 },
                                        borderRadius: 4,
                                        backgroundColor: '#fff3e0',
                                        '& .MuiLinearProgress-bar': { backgroundColor: '#ff9800' }
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ position: 'relative' }}>
                                <Typography
                                    variant="h2"
                                    fontWeight={800}
                                    color="#757575"
                                    gutterBottom
                                    sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' } }}
                                >
                                    4th
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color="text.secondary"
                                    sx={{
                                        mb: 2,
                                        fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' }
                                    }}
                                >
                                    DISPOSE as general waste only as last resort
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={25}
                                    sx={{
                                        height: { xs: 6, sm: 8 },
                                        borderRadius: 4,
                                        backgroundColor: '#f5f5f5',
                                        '& .MuiLinearProgress-bar': { backgroundColor: '#757575' }
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <Box sx={{
                        mt: { xs: 4, sm: 5, md: 6 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 2, sm: 2 },
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <Button
                            component={Link}
                            to="/classify"
                            variant="contained"
                            size={isMobile ? "medium" : "large"}
                            sx={{
                                px: { xs: 3, sm: 4 },
                                py: { xs: 1.5, sm: 2 },
                                background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #45a049 0%, #7cb342 100%)'
                                }
                            }}
                        >
                            Classify Your Waste
                        </Button>
                        <Button
                            component={Link}
                            to="/tips"
                            variant="outlined"
                            size={isMobile ? "medium" : "large"}
                            sx={{
                                px: { xs: 3, sm: 4 },
                                py: { xs: 1.5, sm: 2 },
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                width: { xs: '100%', sm: 'auto' }
                            }}
                        >
                            Learn More Tips
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default GeneralWastePage;
